import { useCallback, useEffect, useState } from "react";
import { getSignedUrl } from "../services/Firebase";

interface UseFirebaseUrlResult {
  url: string | null;
  loading: boolean;
  error: Error | null;
  retry: () => void;
}

export function useFirebaseUrl(
  path: string | null | undefined,
): UseFirebaseUrlResult {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => setAttempt(value => value + 1), []);

  useEffect(() => {
    if (!path) {
      setLoading(false);
      setUrl(null);
      setError(null);
      return;
    }

    let isMounted = true;
    const fetchUrl = async () => {
      setLoading(true);
      setError(null);
      setUrl(null);
      let timeout: ReturnType<typeof setTimeout> | undefined;
      try {
        const downloadUrl = await Promise.race([
          getSignedUrl(path),
          new Promise<never>((_, reject) => {
            timeout = setTimeout(
              () =>
                reject(new Error("Image preview timed out. Please try again.")),
              15000,
            );
          }),
        ]);
        if (isMounted) {
          setUrl(downloadUrl);
        }
      } catch (err: any) {
        if (isMounted) {
          console.error(`Error loading Firebase URL for path: ${path}`, err);
          setError(err);
        }
      } finally {
        clearTimeout(timeout);
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchUrl();

    return () => {
      isMounted = false;
    };
  }, [path, attempt]);

  return { url, loading, error, retry };
}
